import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChevronDown, ChevronRight, Bell } from 'lucide-react';
import logo from '../assets/DifferentialPay_tp.svg';
import './Sidebar.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function Sidebar() {
  const { logout, user, token, isAdmin, canApprovePayments } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  
  const isReportsActive = location.pathname.startsWith('/reports');
  const [reportsExpanded, setReportsExpanded] = useState(isReportsActive);

  // Settings visible to Admin and Payment Approver
  const canAccessSettings = isAdmin() || canApprovePayments();

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/api/notifications/unread-count/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.unread_count);
        }
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    logout();
  };

  const toggleReports = () => {
    setReportsExpanded(!reportsExpanded);
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <img src={logo} alt="Differential Pay" className="sidebar-logo" />
      </div>
      {process.env.REACT_APP_ENVIRONMENT !== 'Production' && (
  <div style={{
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '500',
    textAlign: 'center',
    borderBottom: '1px solid #fcd34d',
  }}>
    ⚠️ TEST APPLICATION
  </div>
)}
      
      <nav className="sidebar-nav">
        <NavLink 
          to="/dashboard" 
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <span className="nav-icon">🏠</span>
          <span className="nav-text">Dashboard</span>
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </NavLink>

        <NavLink 
          to="/suppliers" 
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <span className="nav-icon">👥</span>
          <span className="nav-text">Suppliers</span>
        </NavLink>
        
        <NavLink 
          to="/batches" 
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <span className="nav-icon">💳</span>
          <span className="nav-text">Payments</span>
        </NavLink>

        {/* Reports Section with Sub-menu */}
        <div className={`nav-section ${isReportsActive ? 'active' : ''}`}>
          <button 
            className={`nav-item nav-section-header ${isReportsActive ? 'active' : ''}`}
            onClick={toggleReports}
          >
            <span className="nav-icon">📊</span>
            <span className="nav-text">Reports</span>
            <span className="nav-chevron">
              {reportsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          </button>
          
          {reportsExpanded && (
            <div className="nav-submenu">
              <NavLink 
                to="/reports/supplier-summary" 
                className={({ isActive }) => `nav-subitem ${isActive ? 'active' : ''}`}
              >
                Supplier Summary
              </NavLink>
              <NavLink 
                to="/reports/cost-centre-summary" 
                className={({ isActive }) => `nav-subitem ${isActive ? 'active' : ''}`}
              >
                Cost Centre Summary
              </NavLink>
              <NavLink 
                to="/reports/payment-detail" 
                className={({ isActive }) => `nav-subitem ${isActive ? 'active' : ''}`}
              >
                Payment Detail
              </NavLink>
              <NavLink 
                to="/reports/monthly-overview" 
                className={({ isActive }) => `nav-subitem ${isActive ? 'active' : ''}`}
              >
                Monthly Overview
              </NavLink>
              <NavLink 
                to="/reports/outstanding" 
                className={({ isActive }) => `nav-subitem ${isActive ? 'active' : ''}`}
              >
                Outstanding Payments
              </NavLink>
            </div>
          )}
        </div>

        <NavLink 
          to="/help" 
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <span className="nav-icon">❓</span>
          <span className="nav-text">Help</span>
        </NavLink>

        {/* Settings - Admin and Payment Approver only */}
        {canAccessSettings && (
          <NavLink 
            to="/settings" 
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span className="nav-icon">⚙️</span>
            <span className="nav-text">Settings</span>
          </NavLink>
        )}
      </nav>
      
      <div className="sidebar-footer">
        {user && <div className="user-info">Logged in as {user.first_name} {user.last_name}</div>}
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;